import Papa from "papaparse";
import * as XLSX from "xlsx";

import {
  BULK_UPLOAD_NOTES,
  EMPLOYEE_FIELDS,
  getCanonicalBulkHeaders,
  getSampleRows,
} from "./employeeFieldSchema";

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function toCsv(rows, headers) {
  return Papa.unparse(rows, { columns: headers, header: true, skipEmptyLines: true });
}

export function parseCsv(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => String(h || "").trim(),
      complete: (res) => resolve({ headers: res.meta.fields || [], rows: res.data || [] }),
      error: (err) => reject(err),
    });
  });
}

export async function parseXlsx(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
  const headers = rows.length ? Object.keys(rows[0]) : [];
  return { headers, rows };
}

export function buildSampleCsvBlob() {
  const headers = getCanonicalBulkHeaders({ includeSecondaryNameColumns: true });
  const rows = getSampleRows();
  const csv = toCsv(rows, headers);
  return new Blob([csv], { type: "text/csv;charset=utf-8" });
}

function buildInstructionsRows() {
  const headerRow = ["Column", "Required", "Type", "Description", "Example"];
  const groupTitles = {
    Basic: "Step 1 — Personal",
    Organization: "Step 2 — Organization",
    Compensation: "Step 3 — Compensation",
    Banking: "Step 4 — Banking (optional — leave blank to skip)",
    Statutory: "Step 5 — Statutory (optional — leave blank to skip)",
  };
  const dataRows = [];
  let lastGroup = null;
  for (const f of EMPLOYEE_FIELDS.filter((field) => field.bulk)) {
    if (f.group !== lastGroup) {
      lastGroup = f.group;
      dataRows.push([]);
      dataRows.push([groupTitles[f.group] || f.group]);
    }
    dataRows.push([
      f.key,
      f.bulk?.required ? "Yes" : "No",
      f.type,
      f.hint || f.label,
      f.example ?? "",
    ]);
    if (f.bulk?.secondaryNameKey) {
      dataRows.push([
        f.bulk.secondaryNameKey,
        f.bulk?.required ? "Yes (if no UUID)" : "No",
        "name",
        `Alternative to ${f.key} — matched against setup list`,
        "",
      ]);
    }
  }

  return [
    headerRow,
    ["employee_code", "No (auto)", "—", "Auto-generated on save — do not include", "BA0001"],
    ...dataRows,
    [],
    ["Notes"],
    ...BULK_UPLOAD_NOTES.map((n) => [n]),
  ];
}

export function buildSampleXlsxBlob() {
  const headers = getCanonicalBulkHeaders({ includeSecondaryNameColumns: true });
  const rows = getSampleRows();

  const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
  XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

  const instructions = XLSX.utils.aoa_to_sheet(buildInstructionsRows());

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Employees");
  XLSX.utils.book_append_sheet(wb, instructions, "Field Guide");
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function buildErrorReportCsvBlob({ canonicalRows, rowErrors }) {
  const headers = ["row", "error", ...Object.keys((canonicalRows || [])[0] || {})];
  const rows = (rowErrors || []).map((e) => ({
    row: e.row,
    error: e.error,
    ...(canonicalRows?.[e.rowIndex] || {}),
  }));
  const csv = toCsv(rows, headers);
  return new Blob([csv], { type: "text/csv;charset=utf-8" });
}

